import { Button, Card, Dropdown, Image, message, Space, Spin } from "antd";
import { StepProps as StepPropsAntd } from "antd/lib/steps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import TitleSection from "../../components/TitleSection";
import { httpRequest } from "../../helpers/api";
import { getErrorMessage } from "../../helpers/errorHandler";
import { BaseResponseProps } from "../../types/config.type";
import {
	EListingPDFOrientation,
	EListingStatus,
	ListingProperties,
	PortalListingResultDto,
} from "../../types/listing.type";
import DetailListingInformation from "./DetailComponents/ListingInformation";
import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import { downloadFile } from "../../helpers/download";
import useAuth from "../../hooks/useAuth";
import useBreadcrumbs from "../../hooks/useBreadcrumbs";
import styled from "styled-components";
import { successModal } from "../../helpers/modal";
import { getMainEmailAddress } from "../../helpers/profile";

const MAPPING_STEP = {
	"seller-information": 0,
	"listing-information": 1,
	"portal-information": 2,
};

interface Params {
	listingId: string;
	index?: keyof typeof MAPPING_STEP;
}
export interface StepProps {
	listingId: string;
	listing?: ListingProperties;
	listingPortals?: PortalListingResultDto[];
}
const MaskingListingDetail: React.FC = () => {
	const { setBreadcrumb } = useBreadcrumbs();
	const { listingId, index = "seller-information" } = useParams<Params>();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [listing, setListing] = useState<ListingProperties>();
	const [listingPortals, setListingPortals] = useState<PortalListingResultDto[]>([]);
	const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
	const { employee, user } = useAuth();
	const hasListingPortal = useMemo(() => {
		return (
			listingPortals !== undefined &&
			listingPortals.length > 0 &&
			listingPortals.some((v) => v.portalListingId !== undefined)
		);
	}, [listingPortals]);

	const loadListing = useCallback(async () => {
		setIsLoading(true);
		try {
			const endpoint = "listings";

			let { data } = await httpRequest.get<BaseResponseProps<ListingProperties>>(
				`${endpoint}/${listingId}?includeData=user,profile,office,listingRenewHistory,listingTransferHistory,portalListing,closingId`
			);
			let bc = [
				{
					label: data.payload.title,
					origin: listingId,
				},
			];
			setBreadcrumb(bc);
			setListing(data.payload);
			const dataListing = data.payload;
			if (dataListing != null && dataListing.portalListing?.some((v) => v.portalListingId !== undefined)) {
				for (const portalListing of dataListing.portalListing) {
					try {
						const { data } = await httpRequest.get<BaseResponseProps<PortalListingResultDto>>(
							"/portal-listings/" + portalListing.portalListingId + "/current-result"
						);
						setListingPortals((arr) => [...arr, data.payload]);
					} catch (err: any) {
						setListingPortals((arr) => [...arr, err.data as PortalListingResultDto]);
					}
				}
			}
			setIsLoading(false);
		} catch (err) {
			message.error(getErrorMessage(err));
		}

		// eslint-disable-next-line
	}, [listingId]);

	useEffect(() => {
		loadListing();
	}, [loadListing]);

	const doDownloadPDF = async (lang: string) => {
		if (!listing?.pdfOrientation) return message.error("Please wait until loading finished");
		setIsDownloadLoading(true);
		try {
			await httpRequest.get(
				"/listings/" +
					listing.listingId +
					"/pdf/" +
					(listing.pdfOrientation === EListingPDFOrientation.LANDSCAPE ? "landscape" : "portrait") +
					`/${lang}?listingCode=${listing.listingCode}&employeeId=${employee.employeeId}`
			);

			successModal({
				title: "Generating...",
				subtitle: `We still preparing your Masking Listing PDF. We will send your file to email ${getMainEmailAddress(
					user?.profile!
				)} when ready.`,
				isWaiting: true,
				duration: 5000,
			});
		} catch (error) {
			message.error(getErrorMessage(error));
		}
		setIsDownloadLoading(false);
	};

	const stepProps: StepProps = {
		listingId,
		listing,
		listingPortals,
	};

	const stepItems: StepPropsAntd[] = [
		{
			title: "Seller Information",
			status: index !== "seller-information" ? "wait" : "process",
		},
		{
			title: "Listing Information",
			status: index !== "listing-information" ? "wait" : "process",
		},
	];
	if (hasListingPortal) {
		stepItems.push({
			title: "Portal Information",
			status: index !== "portal-information" ? "wait" : "process",
		});
	}

	const LANG_EN = (
		<LangItem>
			<Image
				src="/en-flag.png"
				alt="ENG logo"
				width={24}
				height={15}
			/>
			<span>ENG</span>
		</LangItem>
	);
	const LANG_ID = (
		<LangItem>
			<Image
				src="/id-flag.png"
				alt="IND logo"
				width={24}
				height={15}
			/>
			<span>IDN</span>
		</LangItem>
	);

	const LANG_ITEMS = [
		{
			label: LANG_ID,
			key: "id",
		},
		{
			label: LANG_EN,
			key: "en",
		},
	];

	const handleDownload = (key: string) => {
		doDownloadPDF(key);
	};

	return (
		<React.Fragment>
			<TitleSection
				icon="back"
				title="Detail Masking Listing"
				// rightAction={
				// 	<Space direction="horizontal">
				// 		<Dropdown
				// 			menu={{
				// 				items: LANG_ITEMS,
				// 				onClick: ({ key }) => handleDownload(key),
				// 			}}
				// 			disabled={listing?.status === EListingStatus.OPEN}
				// 		>
				// 			<Button
				// 				type="link"
				// 				icon={<DownloadOutlined />}
				// 				loading={isDownloadLoading}
				// 			>
				// 				Download PDF <DownOutlined />
				// 			</Button>
				// 		</Dropdown>
				// 	</Space>
				// }
			/>
			<Spin spinning={isLoading}>
				<Card
					bordered={false}
					size="small"
				>
					<DetailListingInformation
						{...stepProps}
						hasListingPortal={hasListingPortal}
					/>
				</Card>
			</Spin>
		</React.Fragment>
	);
};

export const LangItem = styled.span`
	display: inline-flex;
	gap: 10px;
	justify-content: center;
	align-items: center;
	font-weight: bold;
	cursor: pointer;
`;

export default MaskingListingDetail;
